import type {
  ComissaoLancamento, ComissaoSaque, Empresa, Escritorio, Indicacao, Instalacao, Usuario, UsuarioAdmin, VisaoGeral,
} from './tipos';
import { chaveContexto, type OpcaoContexto } from './contexto';

const BASE = '/api/portal';

let token: string | null = localStorage.getItem('conti9_token');

export function guardarToken(t: string | null) {
  token = t;
  if (t) localStorage.setItem('conti9_token', t);
  else localStorage.removeItem('conti9_token');
}

export function temToken() {
  return Boolean(token);
}

// Com qual chapéu a pessoa entrou (admin / escritório / empresa) — ver
// ../contexto.ts. Vai num cabeçalho porque é por requisição, não por
// sessão: trocar de contexto não pede novo código.
let contexto: OpcaoContexto | null = JSON.parse(localStorage.getItem('conti9_contexto') || 'null');

export function guardarContexto(c: OpcaoContexto | null) {
  contexto = c;
  if (c) localStorage.setItem('conti9_contexto', JSON.stringify(c));
  else localStorage.removeItem('conti9_contexto');
}

export function lerContexto(): OpcaoContexto | null {
  return contexto;
}

/**
 * O erro do portal já vem escrito para gente ler (`mensagem`). A tela
 * mostra esse texto direto em vez de inventar um genérico — quem está no
 * suporte precisa da frase que o servidor realmente disse.
 */
export class ErroApi extends Error {
  status: number;
  codigo: string;
  esperarSegundos?: number;

  constructor(mensagem: string, status: number, codigo: string, esperarSegundos?: number) {
    super(mensagem);
    this.status = status;
    this.codigo = codigo;
    this.esperarSegundos = esperarSegundos;
  }
}

async function req<T>(caminho: string, opcoes: RequestInit = {}): Promise<T> {
  const cabecalhos: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opcoes.headers as Record<string, string> | undefined),
  };
  if (token) cabecalhos.Authorization = `Bearer ${token}`;
  if (contexto) cabecalhos['X-Conti9-Contexto'] = chaveContexto(contexto);

  const r = await fetch(`${BASE}${caminho}`, { ...opcoes, headers: cabecalhos });
  const corpo = await r.json().catch(() => ({}));
  if (!r.ok) {
    throw new ErroApi(
      corpo.mensagem || `Falha ${r.status}`,
      r.status,
      corpo.erro || 'desconhecido',
      corpo.esperarSegundos,
    );
  }
  return corpo as T;
}

const corpo = (body: unknown) => JSON.stringify(body);

/**
 * Download autenticado (XML, pacote do mes): o arquivo vem pelo fetch com o
 * token e vira um link temporario. Link direto nao mandaria o Authorization.
 */
export async function baixar(caminho: string, nomeArquivo: string): Promise<void> {
  const cabecalhos: Record<string, string> = {};
  if (token) cabecalhos.Authorization = `Bearer ${token}`;
  if (contexto) cabecalhos['X-Conti9-Contexto'] = chaveContexto(contexto);
  const r = await fetch(`${BASE}${caminho}`, { headers: cabecalhos });
  if (!r.ok) {
    const corpoErro = await r.json().catch(() => ({}));
    throw new ErroApi(corpoErro.mensagem || `Falha ${r.status}`, r.status, corpoErro.erro || 'desconhecido');
  }
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeArquivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Envio de arquivo cru (ate 100 MB): o corpo sao os bytes; tipo e nome vao em cabecalho. */
export async function enviarArquivo<T>(caminho: string, arquivo: File, tipo: string): Promise<T> {
  const cabecalhos: Record<string, string> = {
    'Content-Type': 'application/octet-stream',
    'X-Tipo-Arquivo': tipo,
    'X-Nome-Arquivo': encodeURIComponent(arquivo.name),
  };
  if (token) cabecalhos.Authorization = `Bearer ${token}`;
  if (contexto) cabecalhos['X-Conti9-Contexto'] = chaveContexto(contexto);
  const r = await fetch(`${BASE}${caminho}`, { method: 'POST', headers: cabecalhos, body: arquivo });
  const corpoResp = await r.json().catch(() => ({}));
  if (!r.ok) {
    throw new ErroApi(corpoResp.mensagem || `Falha ${r.status}`, r.status, corpoResp.erro || 'desconhecido');
  }
  return corpoResp as T;
}

export type ConfigItem = {
  key: string; envVar: string; label: string; kind: 'text' | 'number' | 'secret';
  group: string; hint: string; origem: 'painel' | 'env' | 'unset';
  overridden: boolean; configured: boolean; value: string; preview: string;
};
export type ConfigView = {
  groups: { group: string; items: ConfigItem[] }[];
  locked: { key: string; label: string; configured: boolean }[];
};

export const api = {
  // ── entrada ──
  solicitarCodigo: (celular: string) =>
    req<{
      celular: string;
      celularFormatado: string;
      mensagem: string;
      validadeMinutos?: number;
      codigoDeBanca?: string;
    }>('/auth/solicitar-codigo', { method: 'POST', body: corpo({ celular }) }),

  confirmarCodigo: (celular: string, codigo: string) =>
    req<{ token: string; usuario: Usuario }>('/auth/confirmar-codigo', {
      method: 'POST', body: corpo({ celular, codigo }),
    }),

  entrarCentral: (token: string) =>
    req<{ token: string; usuario: Usuario; empresaId: number }>('/central/entrar', {
      method: 'POST', body: corpo({ token }),
    }),

  eu: () => req<{ usuario: Usuario }>('/eu'),

  // ── configurações/integrações (admin) ──
  getConfig: () => req<ConfigView>('/config'),
  salvarConfig: (values: Record<string, string | null>) =>
    req<ConfigView & { ok: true; alterados: string[]; ignorados: string[] }>('/config', {
      method: 'PUT', body: corpo({ values }),
    }),
  testarIntegracao: (alvo: string) =>
    req<{ ok: boolean; detalhe: string }>(`/config/testar/${alvo}`, { method: 'POST' }),

  // ── gerenciamento ──
  visaoGeral: () => req<VisaoGeral>('/admin/visao-geral'),

  leituraEmpresas: () => req<{ empresas: EmpresaLeitura[] }>('/leitura/empresas'),
  leituraCompetencias: (empresaId: number) =>
    req<{ competencias: CompetenciaLeitura[] }>(`/leitura/empresas/${empresaId}/competencias`),
  leituraDocumentos: (empresaId: number, competencia: string) =>
    req<{ documentos: DocumentoLeitura[] }>(`/leitura/empresas/${empresaId}/documentos?competencia=${competencia}`),
  leituraArquivosFiscais: (empresaId: number, competencia: string) =>
    req<{ arquivos: ArquivoFiscal[] }>(`/leitura/empresas/${empresaId}/competencias/${competencia}/arquivos-fiscais`),
  leituraArquivosContador: (empresaId: number, competencia: string) =>
    req<{ arquivos: ArquivoContador[] }>(`/leitura/empresas/${empresaId}/competencias/${competencia}/arquivos-contador`),
  enviarArquivoContador: (empresaId: number, competencia: string, tipo: TipoArquivoContador, arquivo: File) =>
    enviarArquivo<{ id: number; duplicado: boolean }>(
      `/leitura/empresas/${empresaId}/competencias/${competencia}/arquivos-contador`, arquivo, tipo,
    ),
  enviarXmlDocumentos: (empresaId: number, competencia: string, arquivo: File) =>
    enviarArquivo<{ id: number; duplicado: boolean }>(
      `/leitura/empresas/${empresaId}/competencias/${competencia}/xml-documentos`, arquivo, 'XML_DOCUMENTOS',
    ),
  pendencias: () => req<Pendencias>('/admin/pendencias'),
  confirmarEscritorio: (id: number) => req<{ ok: true }>(`/admin/escritorios/${id}/confirmar`, { method: 'POST', body: corpo({}) }),
  resolverConvite: (id: number, acao: 'aprovar' | 'recusar') =>
    req<{ ok: true }>(`/admin/convites/${id}/${acao}`, { method: 'POST', body: corpo({}) }),
  resolverTroca: (id: number, acao: 'aprovar' | 'recusar') =>
    req<{ ok: true }>(`/admin/trocas/${id}/${acao}`, { method: 'POST', body: corpo({}) }),
  escritorios: (filtros: { sistema?: number; semSistema?: boolean } = {}) => {
    const qs = new URLSearchParams();
    if (filtros.sistema) qs.set('sistema', String(filtros.sistema));
    if (filtros.semSistema) qs.set('semSistema', '1');
    const s = qs.toString();
    return req<{ escritorios: Escritorio[] }>(`/admin/escritorios${s ? `?${s}` : ''}`);
  },
  criarEscritorio: (dados: { nome: string; cnpj?: string; retencaoMeses?: number; observacao?: string }) =>
    req<{ escritorio: Escritorio }>('/admin/escritorios', { method: 'POST', body: corpo(dados) }),
  alterarEscritorio: (id: number, dados: Record<string, unknown>) =>
    req<{ escritorio: Escritorio }>(`/admin/escritorios/${id}`, { method: 'PATCH', body: corpo(dados) }),

  empresas: (filtros: { escritorio?: number; busca?: string } = {}) => {
    const qs = new URLSearchParams();
    if (filtros.escritorio) qs.set('escritorio', String(filtros.escritorio));
    if (filtros.busca) qs.set('busca', filtros.busca);
    const s = qs.toString();
    return req<{ empresas: Empresa[] }>(`/admin/empresas${s ? `?${s}` : ''}`);
  },
  criarEmpresa: (dados: { cnpj: string; razaoSocial: string; escritorioId: number; nomeFantasia?: string }) =>
    req<{ empresa: Empresa }>('/admin/empresas', { method: 'POST', body: corpo(dados) }),
  alterarEmpresa: (id: number, dados: Record<string, unknown>) =>
    req<{ empresa: Empresa }>(`/admin/empresas/${id}`, { method: 'PATCH', body: corpo(dados) }),

  instalacoes: () => req<{ instalacoes: Instalacao[] }>('/admin/instalacoes'),
  criarInstalacao: (dados: { nome: string; escritorioId?: number; semEscritorio?: boolean }) =>
    req<{ instalacao: Instalacao; chave: string }>('/admin/instalacoes', { method: 'POST', body: corpo(dados) }),
  alterarInstalacao: (id: number, dados: { ativo: boolean }) =>
    req<{ instalacao: Instalacao }>(`/admin/instalacoes/${id}`, { method: 'PATCH', body: corpo(dados) }),

  usuarios: () => req<{ usuarios: UsuarioAdmin[] }>('/admin/usuarios'),
  criarUsuario: (dados: {
    nome: string; celular: string; email?: string; isAdmin?: boolean;
    escritorioId?: number; empresaId?: number; papel?: 'responsavel' | 'contador';
  }) => req<{ usuario: UsuarioAdmin }>('/admin/usuarios', { method: 'POST', body: corpo(dados) }),
  alterarUsuario: (id: number, dados: Record<string, unknown>) =>
    req<{ usuario: UsuarioAdmin }>(`/admin/usuarios/${id}`, { method: 'PATCH', body: corpo(dados) }),
  vincular: (id: number, dados: {
    escritorioId?: number; empresaId?: number;
    papel?: 'responsavel' | 'contador'; remover?: boolean;
  }) => req<{ ok: true }>(`/admin/usuarios/${id}/vinculos`, { method: 'POST', body: corpo(dados) }),

  // ── indique e ganhe (contador/funcionário) ──
  indicacoes: () => req<IndicacoesView>('/indicacoes'),
  criarIndicacao: (dados: { nomeProspecto: string; celularProspecto?: string; observacao?: string }) =>
    req<Indicacao>('/indicacoes', { method: 'POST', body: corpo(dados) }),
  solicitarSaque: () => req<{ id: number; valor: string; status: string; criadoEm: string }>(
    '/indicacoes/saque', { method: 'POST', body: corpo({}) },
  ),

  // ── indique e ganhe (admin) ──
  adminIndicacoes: () => req<{ indicacoes: IndicacaoAdmin[] }>('/admin/indicacoes'),
  converterIndicacao: (id: number, empresaId: number) =>
    req<{ ok: true }>(`/admin/indicacoes/${id}/converter`, { method: 'POST', body: corpo({ empresaId }) }),
  cancelarIndicacao: (id: number) =>
    req<{ ok: true }>(`/admin/indicacoes/${id}/cancelar`, { method: 'POST', body: corpo({}) }),
  definirMensalidade: (empresaId: number, valor: number | null) =>
    req<{ ok: true; valor: number | null }>(`/admin/empresas/${empresaId}/mensalidade`, { method: 'PUT', body: corpo({ valor }) }),
  adminSaques: () => req<{ saques: SaqueAdmin[] }>('/admin/saques'),
  resolverSaque: (id: number, acao: 'pagar' | 'recusar') =>
    req<{ ok: true }>(`/admin/saques/${id}/${acao}`, { method: 'POST', body: corpo({}) }),

  // ── chamados ──
  chamados: () => req<{ chamados: Chamado[]; souSuporte: boolean }>('/chamados'),
  abrirChamado: (dados: { empresaId: number; descricao: string }) =>
    req<{ id: number }>('/chamados', { method: 'POST', body: corpo(dados) }),
  atenderChamado: (id: number) => req<{ ok: true; acessoAte: string }>(`/chamados/${id}/atender`, { method: 'POST', body: corpo({}) }),
  resolverChamado: (id: number) => req<{ ok: true }>(`/chamados/${id}/resolver`, { method: 'POST', body: corpo({}) }),
  cancelarChamado: (id: number) => req<{ ok: true }>(`/chamados/${id}/cancelar`, { method: 'POST', body: corpo({}) }),
};

/** O que a softhouse (GestorIA) mandou e espera um administrador (008_vinculo_softhouse.sql). */
export interface Pendencias {
  escritorios: { id: number; nome: string; cnpj: string; criadoEm: string; empresas: number }[];
  convites: {
    id: number; nome: string; celular: string; email: string | null; crc: string | null; criadoEm: string;
    escritorioId: number; escritorio: string; escritorioCnpj: string; empresa: string | null; empresaCnpj: string | null;
  }[];
  trocas: {
    id: number; criadoEm: string; empresa: string; empresaCnpj: string;
    escritorioAtual: string; escritorioAtualCnpj: string; escritorioNovo: string; escritorioNovoCnpj: string;
  }[];
}

export interface EmpresaLeitura {
  id: number; cnpj: string; razaoSocial: string; nomeFantasia: string | null; escritorio: string; ultimaCompetencia: string | null;
}
export interface CompetenciaLeitura {
  competencia: string; status: string; qtdNfe: number; qtdNfce: number; valorTotal: string;
  ultimaCapturaEm: string | null; divergencias: number;
}
export interface DocumentoLeitura {
  id: number; tipo: string; sentido: 'entrada' | 'saida'; chave44: string; numero: number; serie: number;
  emitidoEm: string; valorTotal: string | null; situacao: string; destinatario: string | null;
  destinatarioDoc: string | null; natureza: string | null;
}

export interface ArquivoFiscal {
  id: number; tipo: 'EFD_ICMS_IPI' | 'EFD_CONTRIBUICOES' | 'INVENTARIO' | 'XML_DOCUMENTOS'; finalidade: 'original' | 'retificadora';
  nomeArquivo: string; tamanhoBytes: number; recebidoEm: string; resumo: ResumoSped | null;
}

export interface LinhaLivro {
  cfop: string; valorContabil: string; baseCalculo: string; icms: string; isentas: string; outras: string;
}

export interface NotaE113 {
  cnpjParticipante?: string; modelo?: string; serie?: string; numero?: string; data?: string; chave?: string; valor?: string;
}

export interface ResumoSped {
  cabecalho?: { cnpj?: string; ie?: string; periodoInicio?: string; periodoFim?: string; perfil?: string; finalidade?: string };
  livros?: { entradas?: LinhaLivro[]; saidas?: LinhaLivro[] };
  e110?: Partial<Record<
    | 'debitos' | 'creditos' | 'ajustesDebito' | 'ajustesCredito' | 'estornosCredito' | 'estornosDebito'
    | 'saldoCredorAnterior' | 'deducoes' | 'saldoDevedor' | 'saldoCredor' | 'aRecolher' | 'saldoCredorTransportar',
    string>>;
  e111?: { codigo: string; valor: string; notasE113?: NotaE113[] }[];
  e116?: { codigo: string; codigoReceita?: string; valor: string; vencimento?: string }[];
  inventario?: { itens?: number; valorTotal?: string };
  documentos?: { c100?: number; d100?: number; nfce?: number };
  semExtratoFronteira?: boolean;
}

export type TipoArquivoContador = 'EXTRATO_FRONTEIRA' | 'LISTA_CTE' | 'LIVRO_ESCRITURADO' | 'OUTRO';
export interface ArquivoContador {
  id: number; tipo: TipoArquivoContador; nomeArquivo: string; tamanhoBytes: number;
  enviadoEm: string; enviadoPor: string;
  retorno: { situacao: 'IMPORTADO' | 'PARA_CONFERIR' | 'RECUSADO'; mensagem: string | null; em: string } | null;
}

export interface IndicacoesView {
  saldo: number;
  saqueMinimo: number;
  indicacoes: Indicacao[];
  lancamentos: ComissaoLancamento[];
  saques: ComissaoSaque[];
}

export interface IndicacaoAdmin {
  id: number;
  nomeProspecto: string;
  celularProspecto: string | null;
  observacao: string | null;
  status: 'pendente' | 'convertida' | 'cancelada';
  criadoEm: string;
  convertidaEm: string | null;
  usuario: { id: number; nome: string; celular: string };
  empresa: { id: number; razaoSocial: string; cnpj: string } | null;
}

export interface SaqueAdmin {
  id: number;
  valor: string;
  status: 'solicitado' | 'pago' | 'recusado';
  criadoEm: string;
  resolvidoEm: string | null;
  usuario: { id: number; nome: string; celular: string };
}

export interface Chamado {
  id: number;
  descricao: string;
  status: 'aberto' | 'em_atendimento' | 'resolvido' | 'cancelado';
  criadoEm: string;
  encerradoEm: string | null;
  acessoAte: string | null;
  empresa: { id: number; razaoSocial: string } | null;
  escritorio: string;
  abertoPor?: { id: number; nome: string };
  atendidoPor: { id: number; nome: string } | null;
}
